import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import competencyApi from "../api/competencyApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import { buildReadableCode } from "../utils/performanceCode";

const CompetenciesPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const [categoryIdFilter, setCategoryIdFilter] = React.useState("");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadCompetencies = can(PERFORMANCE_PERMISSIONS.COMPETENCIES_READ);
  const canCreateCompetencies = can(
    PERFORMANCE_PERMISSIONS.COMPETENCIES_CREATE,
  );

  if (!canReadCompetencies) {
    return (
      <ModulePageShell
        title="Competency Library"
        description="Manage competency definitions and map them to categories."
      >
        <EmptyState message="You do not have permission to view competencies." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.competencies, categoryIdFilter],
    queryFn: () =>
      competencyApi.listCompetencies({
        categoryId: categoryIdFilter || undefined,
        page: 1,
        limit: 50,
      }),
  });

  const categoriesQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.competencyCategories, "for-competencies"],
    queryFn: () => competencyApi.listCategories({ page: 1, limit: 100 }),
  });

  const createMutation = useMutation({
    mutationFn: competencyApi.createCompetency,
    onSuccess: () => {
      toast({ title: "Competency created", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.competencies],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create competency",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      categoryId: "",
      name: "",
      description: "",
      status: "ACTIVE",
    },
  });

  const rows = listQuery.data?.data || [];
  const categories = categoriesQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Competency Library"
      description="Manage competency definitions and map them to categories."
      onRefresh={() => {
        listQuery.refetch();
        categoriesQuery.refetch();
      }}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canCreateCompetencies ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) =>
              createMutation.mutate({
                ...values,
                code: buildReadableCode("COMP", values.name),
              }),
            )}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Create Competency</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Category</FormLabel>
                  <Select
                    placeholder="Select category"
                    {...register("categoryId")}
                  >
                    {categories.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input {...register("name")} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input {...register("description")} />
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Create Competency
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to competencies, but cannot create new competencies." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        <HStack justify="space-between" mb={3}>
          <Text fontWeight="semibold">Competencies</Text>
          <Select
            maxW="320px"
            placeholder="Filter by category"
            value={categoryIdFilter}
            onChange={(e) => setCategoryIdFilter(e.target.value)}
          >
            {categories.map((item) => (
              <option key={item._id} value={item._id}>
                {item.name}
              </option>
            ))}
          </Select>
        </HStack>
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No competencies found." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Competency</Th>
                <Th>Category</Th>
                <Th>Status</Th>
                <Th>Created Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{item.name}</Td>
                  <Td>{item.categoryId?.name || item.categoryId || "-"}</Td>
                  <Td>{item.status || "-"}</Td>
                  <Td>
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString()
                      : "-"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default CompetenciesPage;
